/**
 * FeedbackEnvironment - Tier 1 + Tier 2 진단 데이터 수집 유틸리티
 *
 * 피드백 제출 시 사용자 환경 정보(Tier 1)와
 * 링 버퍼 진단 데이터(Tier 2)를 수집합니다.
 */

import { Capacitor } from '@capacitor/core';
import type { FeedbackEnvironment, FeedbackDiagnostics } from '../../models/Feedback';
import { diagnosticCollector } from '../../services/DiagnosticCollector';
import { My } from '../../services/My';
import package_json from '../../../package.json';

/**
 * Tier 1: 환경 정보 수집 (제출 시점 스냅샷)
 */
export function collectEnvironment(): FeedbackEnvironment {
  const nav = navigator as any;
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
  const perfMemory = (performance as any).memory;
  const timing = performance.timing;

  // 브라우저 정보 파싱
  const ua = navigator.userAgent;
  const browserInfo = parseBrowserInfo(ua);
  const osInfo = parseOsInfo(ua);

  // PWA/네이티브 판별
  let isPWA = false;
  let isNativeApp = false;
  try {
    isNativeApp = Capacitor.isNativePlatform();
    if (!isNativeApp) {
      isPWA = window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true;
    }
  } catch { /* ignore */ }

  // OTA 번들 버전 (localStorage에 저장되어 있는 경우)
  let otaBundleVersion: string | null = null;
  try {
    otaBundleVersion = localStorage.getItem('ota_bundle_version');
  } catch { /* ignore */ }

  // 현재 방 ID (URL에서 추출)
  let currentRoomId: string | null = null;
  const pathMatch = window.location.pathname.match(/\/room\/([^/]+)/);
  if (pathMatch) {
    currentRoomId = pathMatch[1];
  }

  // 워크스페이스 정보
  let activeWorkspaceId: string | null = null;
  let activeWorkspaceName: string | null = null;
  try {
    activeWorkspaceId = localStorage.getItem('activeWorkspaceId');
    activeWorkspaceName = localStorage.getItem('activeWorkspaceName');
  } catch { /* ignore */ }

  return {
    platform: Capacitor.getPlatform(),
    userAgent: ua,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
    touchScreen: navigator.maxTouchPoints > 0,
    appVersion: package_json.version,
    appCommit: __APP_COMMIT__,
    appDescribe: __APP_VERSION__,
    otaBundleVersion,
    osInfo,
    browserInfo,
    language: navigator.language,
    currentUrl: window.location.href,

    // 앱 상태
    isLoggedIn: My.isLoggedIn(),
    activeWorkspaceId,
    activeWorkspaceName,
    currentRoomId,
    workspaceCount: 0, // 구독 중인 워크스페이스 수는 컨텍스트에 따라 설정
    roomCount: My.Rooms.size,
    isPWA,
    isNativeApp,

    // 성능/연결
    onlineStatus: navigator.onLine,
    connectionType: conn?.effectiveType ?? null,
    effectiveDownlink: conn?.downlink ?? null,
    jsHeapUsedMB: perfMemory ? Math.round(perfMemory.usedJSHeapSize / 1048576) : null,
    jsHeapLimitMB: perfMemory ? Math.round(perfMemory.jsHeapSizeLimit / 1048576) : null,
    pageLoadTimeMs: timing?.loadEventEnd > 0 ? timing.loadEventEnd - timing.navigationStart : null,
  };
}

/**
 * Tier 2: 링 버퍼 진단 데이터 스냅샷
 */
export function collectDiagnostics(): FeedbackDiagnostics {
  return diagnosticCollector.snapshot();
}

/**
 * Tier 1 + Tier 2 통합 수집
 */
export function collectAll(): { environment: FeedbackEnvironment; diagnostics: FeedbackDiagnostics } {
  return {
    environment: collectEnvironment(),
    diagnostics: collectDiagnostics(),
  };
}

// ── 브라우저 정보 파싱 ──
function parseBrowserInfo(ua: string): string {
  if (ua.includes('Firefox/')) return `Firefox ${ua.split('Firefox/')[1]?.split(' ')[0]}`;
  if (ua.includes('Edg/')) return `Edge ${ua.split('Edg/')[1]?.split(' ')[0]}`;
  if (ua.includes('Chrome/')) return `Chrome ${ua.split('Chrome/')[1]?.split(' ')[0]}`;
  if (ua.includes('Safari/') && !ua.includes('Chrome')) return `Safari ${ua.split('Version/')[1]?.split(' ')[0]}`;
  return 'Unknown';
}

// ── OS 정보 파싱 ──
function parseOsInfo(ua: string): string {
  if (ua.includes('iPhone') || ua.includes('iPad')) {
    const match = ua.match(/OS (\d+[_\d]*)/);
    return `iOS ${match?.[1]?.replace(/_/g, '.') ?? ''}`.trim();
  }
  if (ua.includes('Android')) {
    const match = ua.match(/Android (\d+[\.\d]*)/);
    return `Android ${match?.[1] ?? ''}`.trim();
  }
  if (ua.includes('Mac OS X')) {
    const match = ua.match(/Mac OS X (\d+[_\d]*)/);
    return `macOS ${match?.[1]?.replace(/_/g, '.') ?? ''}`.trim();
  }
  if (ua.includes('Windows')) {
    const match = ua.match(/Windows NT (\d+[\.\d]*)/);
    return `Windows NT ${match?.[1] ?? ''}`.trim();
  }
  if (ua.includes('Linux')) return 'Linux';
  return 'Unknown';
}
